import Web3 from 'web3';
import type { provider } from 'web3-core';
import type { AbiItem } from 'web3-utils';

import { censorCircular } from '@/utils';
import LOCK_AIRI_ABI from '@/utils/abi/lock-airi.json';
import { read, write } from '@/utils/web3Service';

import type { LockOption } from '../constants';
import { AIG_PERCENT, CREDIT_PERCENT, LOCK_CONTRACT_ADDRESS, NetworkKey } from '../constants';

export type LockedDataTable = {
  id: number | string;
  no: number;
  type: string;
  locked: string;
  airi_reward: string;
  earned_aig: string;
  earned_credit: string;
  unlock_date: number;
  user_id: number;
  status: boolean;
};

export type HistoryResponseBsc = {
  amountLock: string;
  poolId: string;
  status: boolean;
  timeUnlock: string;
  timestamp: string;
  userId: string;
};

export type HistoryResponseOrai = {
  amount_lock: string;
  pool_id: number;
  status: boolean;
  time_unlock: number;
  timestamp: number;
  user_id: number;
};

const initIndexNonce = (total: number) => Array.from(Array(total).keys());

export const getTotalLockWithUserBsc = async (addressUser: string): Promise<string> => {
  return read('nonces', LOCK_CONTRACT_ADDRESS[NetworkKey.Bsc], LOCK_AIRI_ABI as AbiItem[], [
    addressUser,
  ]);
};

export const getListHistoryLockedBsc = async (
  addressUser: string,
  total: number,
): Promise<HistoryResponseBsc[]> => {
  const arrIndex = initIndexNonce(total);
  return read(
    'getUserInfoBatch',
    LOCK_CONTRACT_ADDRESS[NetworkKey.Bsc],
    LOCK_AIRI_ABI as AbiItem[],
    [addressUser, arrIndex],
  );
};

export const getEstimateRewardBsc = async (
  addressUser: string,
  total: number,
): Promise<string[]> => {
  const arrIndex = initIndexNonce(total);
  return read(
    'getEstimateRewardBatch',
    LOCK_CONTRACT_ADDRESS[NetworkKey.Bsc],
    LOCK_AIRI_ABI as AbiItem[],
    [addressUser, arrIndex],
  );
};

export const convertToDataTableBsc = (
  rewards: string[],
  listHistory: HistoryResponseBsc[],
): LockedDataTable[] => {
  return listHistory.map((history, index) => {
    const amountLocked = Web3.utils.fromWei(history.amountLock);
    const airiReward = Web3.utils.fromWei(rewards[index] as string);
    const aigEarned = (Number(amountLocked) * AIG_PERCENT[history.poolId as LockOption]) / 100;
    const creditEarned =
      (Number(amountLocked) * CREDIT_PERCENT[history.poolId as LockOption]) / 100;

    return {
      id: index,
      no: listHistory.length - index,
      type: history.poolId,
      locked: amountLocked,
      earned_aig: aigEarned.toString(),
      earned_credit: creditEarned.toString(),
      unlock_date: Number(history.timeUnlock) * 1000,
      user_id: Number(history.userId),
      status: history.status,
      airi_reward: airiReward,
    };
  });
};

export const onClaimBsc = async ({
  provider,
  account,
  callback,
  nonce,
}: {
  provider: provider;
  account: string;
  callback: (hash: string) => void;
  nonce: number;
}) => {
  return write(
    'claim',
    provider,
    LOCK_CONTRACT_ADDRESS[NetworkKey.Bsc],
    LOCK_AIRI_ABI as AbiItem[],
    account,
    [nonce],
    callback,
  );
};

export const onWithdrawBsc = async ({
  provider,
  account,
  callback,
  nonce,
}: {
  provider: provider;
  account: string;
  callback: (hash: string) => void;
  nonce: number;
}) => {
  return write(
    'withdraw',
    provider,
    LOCK_CONTRACT_ADDRESS[NetworkKey.Bsc],
    LOCK_AIRI_ABI as AbiItem[],
    account,
    [nonce],
    callback,
  );
};

export const getTotalLockWasm = async (addressUser: string) => {
  return window.Wasm.query(
    LOCK_CONTRACT_ADDRESS[NetworkKey.Oraichain],
    censorCircular({
      query_nonce: {
        user: addressUser,
      },
    }),
  );
};

export const getListHistoryLockedWasm = async (addressUser: string, total: number) => {
  const arrIndex = initIndexNonce(total);
  const res = await window.Wasm.query(
    LOCK_CONTRACT_ADDRESS[NetworkKey.Oraichain],
    censorCircular({
      query_user_info_batch: {
        user: addressUser,
        index: arrIndex,
      },
    }),
  );
  return res.data;
};

export const getEstimateRewardWasm = async (addressUser: string, total: number) => {
  const arrIndex = initIndexNonce(total);
  const res = await window.Wasm.query(
    LOCK_CONTRACT_ADDRESS[NetworkKey.Oraichain],
    censorCircular({
      query_estimate_reward_batch: {
        user: addressUser,
        index: arrIndex,
      },
    }),
  );
  return res.data;
};

export const convertToDataTableOrai = (
  rewards: string[],
  listHistory: HistoryResponseOrai[],
): LockedDataTable[] => {
  return listHistory.map((history, index) => {
    const amountLocked = Number(history.amount_lock) * 10 ** -6;
    const airiReward = Number(rewards[index]) * 10 ** -6;
    const aigEarned =
      (Number(amountLocked) * AIG_PERCENT[history.pool_id.toString() as LockOption]) / 100;
    const creditEarned =
      (Number(amountLocked) * CREDIT_PERCENT[history.pool_id.toString() as LockOption]) / 100;

    return {
      id: index,
      no: listHistory.length - index,
      type: history.pool_id.toString(),
      locked: amountLocked.toString(),
      earned_aig: aigEarned.toString(),
      earned_credit: creditEarned.toString(),
      unlock_date: history.time_unlock * 1000,
      user_id: history.user_id,
      status: history.status,
      airi_reward: airiReward.toString(),
    };
  });
};
